import { ChessRules, normalizeUci, type Move, type Result } from '../chess/ChessRules';
import { validatePuzzle, type PuzzleDefinition } from './PuzzleDefinition';
export type PuzzleStatus =
  | 'booting'
  | 'fetching'
  | 'preparing'
  | 'user-turn'
  | 'validating'
  | 'correct-step'
  | 'auto-reply-delay'
  | 'auto-reply-animating'
  | 'incorrect'
  | 'mistake-analyzing'
  | 'mistake-demo'
  | 'solved'
  | 'continuation-classifying'
  | 'continuation-offer'
  | 'continuation-user-turn'
  | 'continuation-engine-turn'
  | 'game-over'
  | 'recoverable-error';
export type Offer =
  | { kind: 'mate'; moves: number }
  | { kind: 'winning'; cp: number }
  | { kind: 'playable' }
  | { kind: 'unavailable' };
export interface Mistake {
  uci: string;
  fenBefore: string;
  atMs: number;
  refutationViewed: boolean;
}
export interface Session {
  /** Stable across engine generations, unique even when the wall clock is coarse/frozen. */
  attemptId?: string;
  puzzle: PuzzleDefinition;
  generation: number;
  fen: string;
  index: number;
  hintLevel: number;
  hintsUsed: number;
  mistakes: Mistake[];
  lastMove?: Move;
  refutation?: { moves: string[]; index: number };
  startedAt: number;
  completedAt?: number;
  solved: boolean;
  revealed: boolean;
  outcome?: 'solved' | 'skipped' | 'revealed' | 'abandoned';
  offer?: Offer;
  message: string;
  requestId?: string;
  result?: Result;
  continuation?: {
    chosen: 'finish-mate' | 'convert' | 'play-out';
    result?: Result | 'exited' | 'resigned' | 'mate-lost';
    plies: number;
    mateLost?: boolean;
  };
}
export type PuzzleState = { [S in PuzzleStatus]: { status: S; session: Session } }[PuzzleStatus];
let attemptSequence = 0;
function freshAttemptId() {
  if (typeof globalThis.crypto?.randomUUID === 'function') return globalThis.crypto.randomUUID();
  // HTTP LAN previews may lack randomUUID. These local record IDs are not credentials.
  const entropy =
    typeof globalThis.crypto?.getRandomValues === 'function'
      ? [...globalThis.crypto.getRandomValues(new Uint32Array(2))]
          .map((n) => n.toString(36))
          .join('-')
      : Math.random().toString(36).slice(2);
  return `${Date.now().toString(36)}-${++attemptSequence}-${entropy}`;
}
export class PuzzleSessionController {
  private listeners = new Set<() => void>();
  private generation = 0;
  private chess!: ChessRules;
  private rewind?: { chess: ChessRules; state: PuzzleState };
  private current!: PuzzleState;
  /**
   * R5: open on the position before the opponent's move, then play it
   * (`preparing` → `playSetup` → `user-turn`), like Lichess and Chess.com.
   */
  animateSetup = false;
  constructor(
    puzzle: PuzzleDefinition,
    private now = () => Date.now(),
  ) {
    this.load(puzzle);
  }
  get snapshot() {
    return this.current;
  }
  get rules() {
    return this.chess.clone();
  }
  subscribe = (fn: () => void) => {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  };
  private publish(status: PuzzleStatus, changes: Partial<Session> = {}) {
    this.current = {
      status,
      session: { ...this.current.session, ...changes, fen: this.chess.fen },
    };
    this.listeners.forEach((fn) => fn());
  }
  load(puzzle: PuzzleDefinition) {
    const valid = validatePuzzle(puzzle);
    this.chess = new ChessRules(valid.fenBefore);
    const setup = this.animateSetup ? undefined : this.chess.play(valid.setupMove);
    this.rewind = undefined;
    this.current = {
      status: setup ? 'user-turn' : 'preparing',
      session: {
        attemptId: freshAttemptId(),
        puzzle: valid,
        generation: ++this.generation,
        fen: this.chess.fen,
        index: 0,
        hintLevel: 0,
        hintsUsed: 0,
        mistakes: [],
        lastMove: setup,
        startedAt: this.now(),
        solved: false,
        revealed: false,
        message: 'Find the best continuation.',
      },
    };
    this.listeners.forEach((fn) => fn());
  }
  /** R5: play the opponent's setup move. Solve time starts when the position is yours. */
  playSetup(generation: number) {
    if (!this.matches(generation) || this.current.status !== 'preparing') return;
    const move = this.chess.play(this.current.session.puzzle.setupMove);
    this.publish('user-turn', { lastMove: move, startedAt: this.now() });
  }
  matches(generation: number, requestId?: string) {
    return (
      generation === this.current.session.generation &&
      (requestId === undefined || requestId === this.current.session.requestId)
    );
  }
  attempt(raw: string): boolean {
    const { status, session: s } = this.current;
    if (!['user-turn', 'continuation-user-turn'].includes(status)) return false;
    let uci: string;
    try {
      uci = normalizeUci(raw);
    } catch {
      return false;
    }
    const trial = this.chess.clone(),
      move = trial.tryMove(uci);
    if (!move) return false;
    if (status === 'continuation-user-turn') {
      this.chess = trial;
      const continuation = {
        ...s.continuation!,
        plies: s.continuation!.plies + 1,
        result: trial.result,
      };
      this.publish(trial.result ? 'game-over' : 'continuation-engine-turn', {
        lastMove: move,
        continuation,
        result: trial.result,
        message: trial.result ? this.resultText(trial.result) : 'Best defense is thinking…',
      });
      return true;
    }
    if (
      uci !== s.puzzle.solutionMoves[s.index] &&
      !(s.puzzle.themes.includes('mateIn1') && trial.mate)
    ) {
      const mistakes = [
        ...s.mistakes,
        {
          uci,
          fenBefore: this.chess.fen,
          atMs: this.now() - s.startedAt,
          refutationViewed: false,
        },
      ].slice(-100);
      // Keep the exact solving position, including repetition and setup history.
      this.rewind = {
        chess: this.chess.clone(),
        state: { status: 'user-turn', session: { ...s, mistakes } },
      };
      this.chess = trial;
      this.publish('incorrect', {
        mistakes,
        lastMove: move,
        hintLevel: 0,
        message: `${move.san} leaves the puzzle line. Checking the best defense…`,
      });
      return true;
    }
    this.chess = trial;
    const done = trial.mate || s.index + 1 >= s.puzzle.solutionMoves.length;
    this.publish(done ? 'solved' : 'auto-reply-delay', {
      lastMove: move,
      index: s.index + 1,
      hintLevel: 0,
      solved: done,
      ...(done
        ? {
            completedAt: this.now(),
            outcome: s.revealed ? 'revealed' : 'solved',
            result: trial.result,
          }
        : {}),
      message: done ? 'Tactic found.' : 'Good move. Keep going.',
    });
    return true;
  }
  reply(generation: number) {
    if (!this.matches(generation) || this.current.status !== 'auto-reply-delay') return;
    const s = this.current.session,
      move = this.chess.play(s.puzzle.solutionMoves[s.index]);
    this.publish('auto-reply-animating', {
      index: s.index + 1,
      lastMove: move,
      message: `Opponent played ${move.san}. Your move.`,
    });
  }
  animationDone(generation: number) {
    if (this.matches(generation) && this.current.status === 'auto-reply-animating')
      this.publish('user-turn');
  }
  retry() {
    const wasMistake = ['incorrect', 'mistake-analyzing', 'mistake-demo'].includes(
      this.current.status,
    );
    if (this.rewind) {
      const r = this.rewind;
      const mistakes = this.current.session.mistakes;
      this.chess = r.chess;
      this.current = { ...r.state, session: { ...r.state.session, mistakes } };
      this.rewind = undefined;
    }
    if (wasMistake)
      this.publish('user-turn', {
        generation: ++this.generation,
        requestId: undefined,
        refutation: undefined,
        message: 'Find the best continuation.',
      });
  }
  hint() {
    if (this.current.status !== 'user-turn') return;
    const s = this.current.session,
      hintLevel = Math.min(3, s.hintLevel + 1);
    this.publish('user-turn', {
      hintLevel,
      hintsUsed: s.hintsUsed + 1,
      message: hintLevel === 1 ? 'Look at the highlighted piece.' : 'Follow the highlighted move.',
    });
    if (hintLevel === 3) {
      this.publish('user-turn', { revealed: true });
      this.attempt(s.puzzle.solutionMoves[s.index]);
    }
  }
  replay() {
    this.load(this.current.session.puzzle);
  }
  end(outcome: 'skipped' | 'abandoned') {
    if (!this.current.session.solved)
      this.publish(this.current.status, { outcome, completedAt: this.now() });
  }
  beginRequest(
    status: 'mistake-analyzing' | 'continuation-classifying' | 'continuation-engine-turn',
    requestId: string,
  ) {
    if (status === 'mistake-analyzing') {
      if (this.current.status !== 'incorrect') return false;
      const s = this.current.session,
        mistakes = s.mistakes.map((m, i) =>
          i === s.mistakes.length - 1 ? { ...m, refutationViewed: true } : m,
        );
      this.publish('incorrect', { mistakes });
    }
    this.publish(status, {
      requestId,
      ...(status === 'mistake-analyzing' ? { message: 'Checking the best defense…' } : {}),
    });
    return true;
  }
  classify(generation: number, requestId: string, offer: Offer) {
    if (this.matches(generation, requestId) && this.current.status === 'continuation-classifying')
      this.publish('continuation-offer', { offer });
  }
  demo(generation: number, requestId: string, line: string[]) {
    if (!this.matches(generation, requestId) || this.current.status !== 'mistake-analyzing') return;
    const trial = this.chess.clone();
    const moves: string[] = [];
    for (const uci of line.slice(0, 8)) {
      if (trial.result || !trial.tryMove(uci)) break;
      moves.push(uci);
    }
    if (!moves.length) {
      if (this.chess.result) {
        this.publish('mistake-demo', {
          refutation: { moves: [], index: 0 },
          message: this.chess.mate
            ? 'This move also gives checkmate. The stored line differs.'
            : this.resultText(this.chess.result),
        });
        return;
      }
      this.failEngine(generation, requestId);
      return;
    }
    this.publish('mistake-demo', { refutation: { moves, index: 0 } });
    this.stepRefutation();
  }
  stepRefutation() {
    const { status, session: s } = this.current;
    const line = s.refutation;
    if (status !== 'mistake-demo' || !line || line.index >= line.moves.length) return false;
    const move = this.chess.play(line.moves[line.index]);
    const names: Record<string, string> = {
      p: 'pawn',
      n: 'knight',
      b: 'bishop',
      r: 'rook',
      q: 'queen',
    };
    const actor = move.color === s.puzzle.solverColor ? 'You' : 'Opponent';
    const message = this.chess.mate
      ? `${move.san} — ${actor === 'You' ? 'you give' : 'opponent gives'} checkmate.`
      : this.chess.result
        ? this.resultText(this.chess.result)
        : move.captured
          ? `${move.san} — ${actor} ${actor === 'You' ? 'capture' : 'captures'} ${actor === 'You' ? 'their' : 'your'} ${names[move.captured]}.`
          : `${move.san} — ${line.index === 0 ? 'best defense found' : actor === 'You' ? 'your best continuation' : 'opponent’s best continuation'}.`;
    this.publish('mistake-demo', {
      lastMove: move,
      refutation: { ...line, index: line.index + 1 },
      message,
    });
    return true;
  }
  startContinuation(chosen: 'finish-mate' | 'convert' | 'play-out') {
    const s = this.current.session;
    if (!s.solved || s.result || s.continuation || s.offer?.kind === 'unavailable') return false;
    this.publish(
      this.chess.turn === s.puzzle.solverColor
        ? 'continuation-user-turn'
        : 'continuation-engine-turn',
      {
        generation: ++this.generation,
        requestId: undefined,
        continuation: { chosen, plies: 0 },
        message:
          chosen === 'finish-mate'
            ? 'Finish the mate. Any legal move is yours.'
            : 'Play the position. Make it your own.',
      },
    );
    return true;
  }
  engineMove(generation: number, requestId: string, uci: string, mateStillPresent?: boolean) {
    if (!this.matches(generation, requestId) || this.current.status !== 'continuation-engine-turn')
      return;
    const move = this.chess.tryMove(uci);
    if (!move) {
      this.failEngine(generation, requestId);
      return;
    }
    const c = this.current.session.continuation!,
      mateLost = c.chosen === 'finish-mate' && mateStillPresent === false;
    this.publish(this.chess.result ? 'game-over' : 'continuation-user-turn', {
      lastMove: move,
      result: this.chess.result,
      continuation: {
        ...c,
        plies: c.plies + 1,
        mateLost: c.mateLost || mateLost,
        result: this.chess.result ?? (mateLost ? 'mate-lost' : c.result),
      },
      message: this.chess.result
        ? this.resultText(this.chess.result)
        : mateLost
          ? 'The forced mate is gone. You can keep playing.'
          : `Engine played ${move.san}. Your move.`,
    });
  }
  exitContinuation(result: 'exited' | 'resigned' = 'exited') {
    const s = this.current.session;
    if (!s.continuation) return;
    this.publish('game-over', {
      generation: ++this.generation,
      requestId: undefined,
      continuation: { ...s.continuation, result },
      message:
        result === 'resigned'
          ? 'Position resigned. Your tactic still counts.'
          : 'Position finished. Your tactic still counts.',
    });
  }
  failEngine(generation: number, requestId: string) {
    if (!this.matches(generation, requestId)) return;
    if (this.rewind) this.retry();
    else if (this.current.session.continuation) this.exitContinuation();
    else this.publish('continuation-offer', { offer: { kind: 'unavailable' } });
    this.publish(this.current.status, {
      message: 'Engine continuation unavailable. Keep solving.',
    });
  }
  private resultText(result: Result) {
    return result === 'checkmate'
      ? this.chess.turn === this.current.session.puzzle.solverColor
        ? 'Checkmate. The engine wins this position. Your tactic still counts.'
        : 'Checkmate. Beautifully finished.'
      : `Game drawn: ${result.replaceAll('-', ' ')}.`;
  }
}
